// @ts-strict-ignore
import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  NoData,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { getTransactionTitle } from 'pages/WalletDetails/hooks/useTitleTransaction'
import { LedgerWalletTransaction } from 'types/ledger/ledgerWalletTransactions'
import { Transaction } from 'types/transaction'
import { RewardsTransactionExport } from './RewardsTransactionExport/RewardsTransactionExport'

export interface RewardsTransactionsListProps {
  transactions: LedgerWalletTransaction[]
  walletId?: string
  loading: boolean
}

export const RewardsTransactionsList = ({
  transactions,
  loading,
  walletId,
}: RewardsTransactionsListProps) => {
  const { t } = useTranslation()
  const hasTransactions = transactions.length > 0

  const tableColumns: DataTableHeaderProps[] = [
    {
      key: 'createdAt',
      label: t('latestTransactions.labels.date'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'type',
      label: t('latestTransactions.labels.type'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'currency',
      label: t('latestTransactions.labels.currency'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'amount',
      label: t('latestTransactions.labels.amount'),
      variant: DataTableCellVariants.TEXT,
    },
  ]

  const tableTransactionsData: DataTableRowData[] = useMemo(() => {
    return transactions.map(transaction => ({
      id: transaction?.transactionId,
      cells: [
        {
          key: 'createdAt',
          variant: DataTableCellVariants.TEXT,
          Text: dayjs(transaction?.createdAt).format(DateFormat.DD_MMM_YYYY_HH_MM_A),
        },
        {
          key: 'type',
          variant: DataTableCellVariants.TEXT,
          Text: getTransactionTitle({
            transaction: { type: transaction?.type } as Transaction,
            t,
          }),
        },
        {
          key: 'currency',
          variant: DataTableCellVariants.TEXT,
          Avatar: (
            <Avatar type="currency" name={transaction?.amount?.currencyCode?.toUpperCase()} />
          ),
          Text: transaction.amount?.currencyCode,
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: `${transaction.amount?.value} ${transaction?.amount?.currencyCode}`,
        },
      ],
    }))
  }, [transactions, t])

  if (loading) {
    return <RowsSkeletonLoader rows={5} />
  }

  return (
    <Box flexCol align="start" gapY={16} className="w-full">
      <Box flex align="center" justify="between" className="w-full">
        <h4 className="text-h4 text-foreground-prominent">{t('rewards.recentTransactions')}</h4>
      </Box>

      <RewardsTransactionExport walletId={walletId} />

      {!hasTransactions && <NoData dataName="transactions" />}

      {hasTransactions && <DataTable headers={tableColumns} data={tableTransactionsData} />}
    </Box>
  )
}
